import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Button, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { Action, Column, Localization, Options } from 'material-table';
import { CsvBuilder } from 'filefy';

import { ClassificationModel } from '../../../../Models/ClassificationModel';
import { BasicPage } from '../../../../shared/components/BasicPage';
import { TableCommon, ITableCommonToolbar } from '../../../../shared/components/TableCommon';
import { TableEmpty } from '../../../../shared/components/TableEmpty';
import ClassificationService from '../../../../Services/ClassificationService';
import { TableLoading } from '../../../../shared/components/TableLoading';
import { SearchStatus } from '../../../../shared/enum/SearchStatus';
import { getTableColumn, handleDisplayDate } from '../../../../shared/operators';
import { ClassificationFilterFields } from '../components/ClassificationFilterFields';
import { IInformationToCorrect, InformationToCorrect } from '../enum/InformationToCorrect';
import { RevisionContext } from '../RevisionContext';
import CategoryService from '../../../../Services/CategoryService';
import { CategoryModelFullDto } from '../../../../Models/CategoryModelFullDto';
import { BatchCorrectionSearchType } from '../enum/BatchCorrectionSearchType';
import { BatchCorrectionContext } from '../BatchCorrectionContext';
import { BatchCorrectionFilterColumnsDialog } from '../components/BatchCorrectionFilterColumnsDialog';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { HomeContext } from '../../../Home/HomeContext';
import { SpotlightBatchCorrection } from './SpotlightBatchCorrection';
import { ContentBatchCorrection } from './ContentBatchCorrection';
import { ExtraParametersBatchCorrection } from './ExtraParametersBatchCorrection';
import { SourceBatchCorrection } from './SourceBatchCorrection';
import { JournalistBatchCorrection } from './JournalistBatchCorrection';
import { KeywordBatchCorrection } from './KeywordBatchCorrection';
import { CoverPresenceBatchCorrection } from './CoverPresenceBatchCorrection';
import { LocationBatchCorretion } from './LocationBatchCorrection';
import { VehicleBatchCorretion } from './VehicleBatchCorretion';
import { OrganogramBatchCorrection } from './OrganogramBatchCorrection';
import { ClassificationStatus } from '../../enum/ClassificationStatus';
import { theme } from '../../../../theme';
import { ExpositionBatchCorrection } from './ExpositionBatchCorrection';

const useStyles = makeStyles((theme: Theme) => createStyles({
    removeButton: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
    },
}));

export interface IConfirmDeleteProps {
    open: boolean,
    confirmMessage: string,
    successMessage: string,
}

export const confirmDeletePropsInitial: IConfirmDeleteProps = {
    open: false,
    confirmMessage: '',
    successMessage: ''
};

export function BatchCorrection() {
    const classes = useStyles();

    const revisionContext = React.useContext(RevisionContext);
    const homeContext = React.useContext(HomeContext);

    const [filterColumnsDialogOpen, setFilterColumnsDialogOpen] = React.useState<boolean>(false);
    const [searchType, setSearchType] = React.useState<BatchCorrectionSearchType | null>(null);
    const [tableColumns, setTableColumns] = React.useState<Column<ClassificationModel | CategoryModelFullDto>[]>([]);

    const [classificationResult, setClassificationResult] = React.useState<ClassificationModel[] | CategoryModelFullDto[]>([]);
    const [totalResultNumber, setTotalResultNumber] = React.useState<number>(0);
    const [exportFilename, setExportFilename] = React.useState<string>('');

    const [deleteConfirm, setDeleteConfirm] = React.useState<IConfirmDeleteProps>(confirmDeletePropsInitial);

    const [informationToCorrect, setInformationToCorrect] = React.useState<IInformationToCorrect | null>(null);

    const [callSearch, setCallSearch] = React.useState<boolean>(false);

    const titleColumn: Column<ClassificationModel | CategoryModelFullDto> =
    {
        title: 'Título',
        field: 'publicationTitle',
        type: 'string',
        align: 'left',
        cellStyle: { minWidth: '200px' },
        render: rowData => getTitleLink(rowData)
    };

    const classificationSearchColumns: Column<ClassificationModel>[] = [
        getTableColumn('ID', 'id', 'numeric', 'left', { maxWidth: '100px' }),
        getTableColumn('ID do metric', 'metricId', 'string', 'left', { maxWidth: '100px' }),
        getTableColumn('Data da publicação', 'publicationDate', 'date', 'left', { maxWidth: '150px' }, 'desc'),
        getTableColumn('Responsável', 'responsible.username', 'string', 'left', { minWidth: '150px' }),
    ];

    const categorizationSearchColumns: Column<CategoryModelFullDto>[] = [
        getTableColumn('ID', 'classificationId', 'numeric', 'left', { maxWidth: '100px' }),
        getTableColumn('ID do metric', 'metricId', 'numeric', 'left', { maxWidth: '100px' }),
        getTableColumn('Categoria', 'index', 'numeric', 'left', { maxWidth: '100px' }),
        getTableColumn('Data da publicação', 'publicationDate', 'date', 'left', { maxWidth: '150px' }, 'asc'),
        getTableColumn('Responsável', 'responsibleUsername', 'string', 'left', { minWidth: '100px' }),
    ];

    const getRowColor = (rowData: ClassificationModel | CategoryModelFullDto): string => {
        if ((searchType == BatchCorrectionSearchType.CLASSIFICATION && rowData.status == ClassificationStatus.REMOVED)
            || (searchType == BatchCorrectionSearchType.CATEGORIZATION && ('active' in rowData) && !rowData.active))
            return theme.palette.text.disabled;
        else
            return theme.palette.text.primary;
    };

    const tableOptions: Options<ClassificationModel | CategoryModelFullDto> = {
        filtering: true,
        toolbar: true,
        draggable: true,
        showTitle: true,
        pageSizeOptions: [10, 50, 100, 200],
        pageSize: 50,
        selection: true,
        selectionProps: () => ({
            color: 'primary'
        }),
        rowStyle: (rowData: ClassificationModel | CategoryModelFullDto) => ({
            color: getRowColor(rowData)
        }),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tableActions: Action<any>[] = [
        {
            icon: 'file_download',
            tooltip: 'Exportar CSV',
            iconProps: { style: { fontSize: '24px', color: theme.palette.primary.main } },
            onClick: (_, rowData) => {
                const columns = [{ ...titleColumn }, ...tableColumns];

                const columnTitles = columns
                    .map(col => col.title);

                const csvData = rowData
                    .filter((data: any) => 'tableData' in data && data.tableData.checked)
                    .map((data: any) => columns.map(col => {
                        const field = col.field ?? '';
                        const nestedFields = field.split('.');

                        if (nestedFields.length == 1)
                            return data[field];
                        else if (nestedFields.length == 2)
                            return data[nestedFields[0]] ? data[nestedFields[0]][nestedFields[1]] : '';
                    }));

                if (csvData) {
                    const builder = new CsvBuilder(exportFilename + '.csv');

                    builder.setDelimeter(';')
                        .setColumns(columnTitles as string[])
                        .addRows(csvData)
                        .exportFile();
                }
                else {
                    homeContext.showWarning('Selecione ao menos uma linha para fazer a exportação dos registros para CSV');
                }
            },
            position: 'toolbarOnSelect'
        }
    ];

    const tableToolbar: ITableCommonToolbar = {
        buttonText: 'FILTRAR COLUNAS',
        onButtonClick: () => setFilterColumnsDialogOpen(true)
    };

    const tableLocalization: Localization = {
        toolbar: {
            nRowsSelected: '',
            exportCSVName: 'Exportar como CSV',
            exportPDFName: 'Exportar como PDF',
            exportTitle: 'Exportar'
        },
    };

    const getTitleLink = (rowData: ClassificationModel | CategoryModelFullDto) => {
        if (rowData.status != ClassificationStatus.REMOVED)
            return (<a href={rowData.clipperLink} target='_blank' rel="noreferrer noopener">{rowData.publicationTitle}</a>);
        else
            return rowData.publicationTitle;
    };

    const classificationSearch = () => {
        return (
            revisionContext.informationToCorrect?.value === InformationToCorrect.JOURNALIST ||
            revisionContext.informationToCorrect?.value === InformationToCorrect.LOCALIZATION ||
            revisionContext.informationToCorrect?.value === InformationToCorrect.VEHICLE ||
            revisionContext.informationToCorrect?.value === InformationToCorrect.COVER_PRESENCE ||
            revisionContext.informationToCorrect?.value === InformationToCorrect.EXPOSITION
        );
    };

    const handleServiceResult = (res: AxiosResponse) => {
        setClassificationResult(res.data);
        if (res.data.length > 0) {
            setTotalResultNumber(res.data.length);
            revisionContext.setSearchStatus(SearchStatus.RESULT);
        }
        else
            revisionContext.setSearchStatus(SearchStatus.EMPTY);
    };

    const handleColumnOnSearch = (baseColumns: Column<any>[]) => {
        const columns: Column<ClassificationModel | CategoryModelFullDto>[] = JSON.parse(JSON.stringify(baseColumns));

        if (revisionContext.informationToCorrect && revisionContext.informationToCorrect.column)
            columns.push(revisionContext.informationToCorrect.column);

        setTableColumns(columns);
    };

    const handleExceptionSearch = (error: AxiosError) => {
        revisionContext.setSearchStatus(SearchStatus.INITIAL);
        homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar as ' +
            (searchType === BatchCorrectionSearchType.CLASSIFICATION ? 'classificações.' : 'categorizações.') +
            'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
    };

    const handleSearch = () => {
        setTableColumns([]);
        revisionContext.setSearchStatus(SearchStatus.LOADING);
        setExportFilename(`Classificações_${revisionContext.clientName}_${handleDisplayDate(new Date())}`);
        setInformationToCorrect(revisionContext.informationToCorrect);

        if (classificationSearch()) {
            setSearchType(BatchCorrectionSearchType.CLASSIFICATION);
            ClassificationService
                .getClassificationsByFilters(revisionContext.filterClassification)
                .then((res: AxiosResponse) => {
                    handleServiceResult(res);
                    handleColumnOnSearch(classificationSearchColumns);
                })
                .catch((error: AxiosError) => { handleExceptionSearch(error); });

        } else {
            setSearchType(BatchCorrectionSearchType.CATEGORIZATION);
            CategoryService
                .getCategoriesByFilters(0, revisionContext.filterClassification)
                .then((res: AxiosResponse) => {
                    handleServiceResult(res);
                    handleColumnOnSearch(categorizationSearchColumns);
                })
                .catch((error: AxiosError) => { handleExceptionSearch(error); });
        }
    };

    const alertNoneSelected = () => {
        homeContext.showWarning(`Ops, nenhuma ${(searchType == BatchCorrectionSearchType.CLASSIFICATION ? 'classificação' : 'categorização')} ` +
            'foi selecionada.');
    };

    const handleRemove = () => {
        const ids = getIdsSelected();
        if (ids.length == 0)
            alertNoneSelected();
        // else if (hasAnyInactive())
        //     alertHasInactive();
        else
            setDeleteConfirm({
                open: true,
                confirmMessage: `Você removerá ${ids.length} 
                    ${searchType == BatchCorrectionSearchType.CLASSIFICATION ? 'classificações' : 'categorizações'} 
                    mas poderá recuperá-las mais tarde se desejar.`,
                successMessage: `${ids.length} 
                    ${searchType == BatchCorrectionSearchType.CLASSIFICATION ? 'Classificações' : 'Categorizações'}
                    removidas com sucesso`
            });
    };

    const onDelete = () => {
        homeContext.setOpenLoadingDialog(true);
        const ids = getIdsSelected();
        if (searchType == BatchCorrectionSearchType.CLASSIFICATION) {
            ClassificationService.deleteBatch(ids)
                .then(() => successDelete())
                .catch((error: AxiosError) => errorDelete(error));
        } else {
            CategoryService.deleteBatch(ids)
                .then(() => successDelete())
                .catch((error: AxiosError) => errorDelete(error));
        }
    };

    const successDelete = () => {
        homeContext.setOpenLoadingDialog(false);
        setCallSearch(true);

        homeContext.showSuccess(deleteConfirm.successMessage);
    };

    const errorDelete = (error: AxiosError) => {
        homeContext.setOpenLoadingDialog(false);
        homeContext.showError(`Ops... Ocorreu um erro e não foi possível remover as ${searchType == BatchCorrectionSearchType.CLASSIFICATION ? 'classificações' : 'categorizações'}. ` +
            'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
    };

    const hasDifferentMediaId = (information: IInformationToCorrect | null) => {
        const mediasIds = getMediasIdsSelecteds();
        const validMediaId = mediasIds.findIndex(s => s != mediasIds[0]);
        if (validMediaId > 0 && information) {
            homeContext.showWarning(`Não é possível corrigir em lote ${information.description} com tipos de mídia diferentes.
                Para isto, acesse a área de Revisão de relatórios. `);
        }
        else {
            revisionContext.setMediaId(mediasIds[0]);
        }
        return (validMediaId > 0);
    };

    const hasAnyInactive = () => {
        const list = JSON.parse(JSON.stringify(classificationResult));
        const ids = list.reduce(function (result: number[], item: any) {
            if (item.tableData && item.tableData.checked && (item.status == ClassificationStatus.REMOVED || ('active' in item && !item.active)))
                result.push(item.id);
            return result;
        }, []);
        return ids.length > 0;
    };

    const alertHasInactive = () => {
        const correctionType = searchType == BatchCorrectionSearchType.CLASSIFICATION ? 'classificação' : 'categorização';
        const pluralCorrectionType = searchType == BatchCorrectionSearchType.CLASSIFICATION ? 'classificações' : 'categorizações';

        homeContext.showWarning(`Ops, você selecionou uma ${correctionType} que já foi removida, verifique a seleção de ${pluralCorrectionType} e tente novamente.`);
    };

    const moreThanOneCategoryPerClassificationChecked = () => {
        const list = JSON.parse(JSON.stringify(classificationResult));
        const checked = list.reduce(function (result: number[], item: any) {
            if (item.tableData && item.tableData.checked)
                result.push(item.classificationId);
            return result;
        }, []);

        const isDuplicate = checked.some(function (item: number, index: number) {
            return checked.indexOf(item) != index;
        });

        return isDuplicate;
    };

    const alertMoreThanOneCategoryPerClassification = () => {
        homeContext.showWarning('Ops, não é possível alterar o organograma de categorizações de uma mesma classificação.');
    };

    const handleCorrection = () => {
        const ids = getCorrecctionIdsSelected();
        if (ids.length == 0)
            alertNoneSelected();
        else if (hasAnyInactive())
            alertHasInactive();
        else {
            revisionContext.setIds(ids);

            if (informationToCorrect) {
                switch (informationToCorrect.value) {
                    case InformationToCorrect.SPOTLIGHT:
                        revisionContext.setSpotlightBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.CONTENT:
                        revisionContext.setContentBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.EXTRA_PARAMETER:
                        revisionContext.setExtraParametersBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.SOURCE:
                        revisionContext.setSourceBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.JOURNALIST:
                        revisionContext.setJournalistBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.KEYWORD:
                        revisionContext.setKeywordBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.ORGANOGRAM:
                        if (!moreThanOneCategoryPerClassificationChecked())
                            revisionContext.setOrganogramBatchOpenDialog(true);
                        else
                            alertMoreThanOneCategoryPerClassification();
                        break;

                    case InformationToCorrect.COVER_PRESENCE:
                        if (!hasDifferentMediaId(informationToCorrect))
                            revisionContext.setCoverPresenceBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.LOCALIZATION:
                        if (!hasDifferentMediaId(informationToCorrect))
                            revisionContext.setLocationBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.VEHICLE:
                        if (!hasDifferentMediaId(informationToCorrect))
                            revisionContext.setVehicleBatchOpenDialog(true);
                        break;

                    case InformationToCorrect.EXPOSITION:
                        if (!hasDifferentMediaId(informationToCorrect))
                            revisionContext.setExpositionBatchOpenDialog(true);
                        break;

                    default:
                        break;
                }
            }
        }
    };

    const getIdsSelected = (): number[] => {
        const list = JSON.parse(JSON.stringify(classificationResult));
        const ids = list.reduce(function (result: number[], item: any) {
            if (item.tableData && item.tableData.checked)
                result.push(item.classificationId);
            return result;
        }, []);
        return ids;
    };
    const getCorrecctionIdsSelected = (): number[] => {
        const list = JSON.parse(JSON.stringify(classificationResult));
        const ids = list.reduce(function (result: number[], item: any) {
            if (item.tableData && item.tableData.checked)
                result.push(item.id);
            return result;
        }, []);
        return ids;
    };

    const getMediasIdsSelecteds = (): number[] => {
        const list = JSON.parse(JSON.stringify(classificationResult));
        const ids = list.reduce(function (result: number[], item: any) {
            if (item.tableData && item.tableData.checked)
                result.push(item.mediaId);
            return result;
        }, []);
        return ids;
    };

    const getResultTable = () => {
        //não retirar esse código, porque ao mexer com as colunas com referência a tela estava travando.
        //foi necessário forçar o JSON parse para retirar as referencias, como consequência, as opções de render, customSort e customFilter
        //das colunas não funciona.
        const columnsTemp: Column<ClassificationModel | CategoryModelFullDto>[] =
            JSON.parse(JSON.stringify([{ ...titleColumn }, ...tableColumns]));
        if (revisionContext.searchStatus == SearchStatus.LOADING) {
            return (
                <Grid item xs={12}>
                    <TableLoading displayText={'Buscando ' +
                        (searchType === BatchCorrectionSearchType.CLASSIFICATION ? 'classificações...' : 'categorizações...')} />
                </Grid>
            );
        }
        else if (revisionContext.searchStatus == SearchStatus.RESULT) {
            return (
                <>
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            {totalResultNumber} {
                                classificationResult.length > 1
                                    ? (searchType === BatchCorrectionSearchType.CLASSIFICATION
                                        ? 'classificações'
                                        : 'categorizações') + ' encontradas'
                                    : (searchType === BatchCorrectionSearchType.CLASSIFICATION
                                        ? 'classificação'
                                        : 'categorização') + ' encontrada'
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TableCommon
                            tableData={classificationResult}
                            tableActions={tableActions}
                            tableColumns={columnsTemp}
                            emptyDataSourceMessage={'Nenhuma ' +
                                (searchType === BatchCorrectionSearchType.CLASSIFICATION
                                    ? 'classificação'
                                    : 'categorização') + ' encontrada'}
                            extraOptions={tableOptions}
                            toolbar={tableToolbar}
                            extraLocalization={tableLocalization}
                        />
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        justify="center"
                        alignItems="center"
                        spacing={2}>
                        <Grid item>
                            <Button
                                onClick={handleCorrection}
                                variant='contained'
                                color='primary'
                                size='large'>
                                CORRIGIR
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={handleRemove}
                                variant='outlined'
                                size='large'
                                className={classes.removeButton}>
                                REMOVER
                            </Button>
                        </Grid>
                    </Grid>
                    <BatchCorrectionFilterColumnsDialog />
                </>
            );
        }
        else if (revisionContext.searchStatus == SearchStatus.EMPTY) {
            return (
                <Grid
                    item
                    xs={12}>
                    <TableEmpty displayText={'Nenhuma ' +
                        (searchType === BatchCorrectionSearchType.CLASSIFICATION
                            ? 'classificação'
                            : 'categorização') + ' encontrada'} />
                </Grid>
            );
        }
    };

    return (
        <BatchCorrectionContext.Provider value={{
            filterColumnsDialogOpen, setFilterColumnsDialogOpen,
            searchType, setSearchType,
            tableColumns, setTableColumns,
            callSearch, setCallSearch
        }}>
            <BasicPage
                title="Correção em lote"
                subtitle="Utilize os filtros abaixo para encontrar as notícias que deseja corrigir ">
                <>
                    <ClassificationFilterFields
                        callbackSearch={handleSearch}
                        callSearch={callSearch}
                        setCallSearch={setCallSearch} />
                    {getResultTable()}
                    <BasicConfirmDialog
                        title={searchType == BatchCorrectionSearchType.CLASSIFICATION
                            ? 'Remover todas as classificações selecionadas?'
                            : 'Remover todas as categorizações selecionadas?'}
                        description={deleteConfirm.confirmMessage}
                        handleFirstButton={() => {
                            setDeleteConfirm(confirmDeletePropsInitial);
                            onDelete();
                        }}
                        handleSecondButton={() => setDeleteConfirm(confirmDeletePropsInitial)}
                        textFirstButton="Remover"
                        textSecondButton="Voltar"
                        open={deleteConfirm.open} />
                    <SpotlightBatchCorrection />
                    <ContentBatchCorrection />
                    <ExtraParametersBatchCorrection />
                    <SourceBatchCorrection />
                    <JournalistBatchCorrection />
                    <KeywordBatchCorrection />
                    <CoverPresenceBatchCorrection />
                    <LocationBatchCorretion />
                    <VehicleBatchCorretion />
                    <OrganogramBatchCorrection />
                    <ExpositionBatchCorrection />
                </>
            </BasicPage>
        </BatchCorrectionContext.Provider>
    );
}
